import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Card, Table, Button, Spinner, Alert, Pagination, Form, Row, Col, Badge, Collapse, Modal } from 'react-bootstrap';
import { FiBarChart2, FiRefreshCw, FiChevronDown, FiChevronRight, FiCalendar, FiMapPin, FiInfo, FiUser, FiEye, FiTrash2 } from 'react-icons/fi';
import { fetchObservationsActivitiesRequest } from '../../store/actions/dataActions';
import { deleteActivityObservation, getAllPartners } from '../../services/api';
import { handleApiError } from '../../utils/errorHandler';
import { useTranslation } from '../../hooks/useTranslation';
import usePersistedSearchState from '../../hooks/usePersistedSearchState';
import 'bootstrap/dist/css/bootstrap.css';
import './AdminActivityObservations.css';

const defaultMeta = {
    total: 0,
    skip: 0,
    limit: 10,
    page: 1,
    total_pages: 0,
};

const getSafe = (obj, path, fallback = '-') => {
    try {
        const value = path.reduce((acc, key) => (acc && acc[key] !== undefined ? acc[key] : undefined), obj);
        return value ?? fallback;
    } catch (error) {
        return fallback;
    }
};

const formatDate = (value) => {
    if (!value) return '-';
    if (value instanceof Date) {
        return value.toLocaleDateString('fr-FR');
    }
    if (typeof value === 'string') {
        const parsed = new Date(value);
        if (!Number.isNaN(parsed.getTime())) {
            return parsed.toLocaleDateString('fr-FR', { year: 'numeric', month: '2-digit', day: '2-digit' });
        }
        return value;
    }
    return value;
};

const EMPTY_ACTIVITY_OBSERVATIONS_FILTERS = {
    policy_code: '',
    activity_start_date: '',
    reservation_number: '',
    client_first_name: '',
    client_last_name: '',
    company_name: '',
};

const AdminActivityObservations = () => {
    const dispatch = useDispatch();
    const { isAuthenticated } = useSelector((state) => state.adminAuth);
    const observationsState = useSelector((state) => state.data.observations.activities);
    const navigate = useNavigate();
    const { t } = useTranslation();

    const renderBooleanBadge = (value) => {
        if (value === null || value === undefined) {
            return <Badge bg="secondary">{t('pages.activityObservations.na')}</Badge>;
        }
        return value ? <Badge bg="success">{t('pages.activityObservations.yes')}</Badge> : <Badge bg="warning" text="dark">{t('pages.activityObservations.no')}</Badge>;
    };

    const {
        filters,
        setFilters,
        appliedFilters,
        setAppliedFilters,
        currentPage,
        setCurrentPage,
        itemsPerPage,
        companyPartnerSearch,
        setCompanyPartnerSearch,
        clearSearchState,
    } = usePersistedSearchState('activity-observations', EMPTY_ACTIVITY_OBSERVATIONS_FILTERS, {
        persistWhen: isAuthenticated,
        withCompanyPartnerSearch: true,
    });
    const [expandedRows, setExpandedRows] = useState({});
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [observationToDelete, setObservationToDelete] = useState(null);
    const [deletingObservationId, setDeletingObservationId] = useState(null);
    const [deleteError, setDeleteError] = useState(null);
    const [deleteSuccess, setDeleteSuccess] = useState(null);
    const [partners, setPartners] = useState([]);
    const [partnersLoading, setPartnersLoading] = useState(false);
    const [partnersError, setPartnersError] = useState(null);
    const [showCompanyPartnerDropdown, setShowCompanyPartnerDropdown] = useState(false);

    const loadObservations = useCallback((page = 1, filtersToUse = null) => {
        const skip = (page - 1) * itemsPerPage;
        const params = {
            skip,
            limit: itemsPerPage,
        };

        const activeFilters = filtersToUse !== null ? filtersToUse : appliedFilters;

        Object.keys(activeFilters).forEach((key) => {
            const value = activeFilters[key];
            if (value != null && String(value).trim() !== '') {
                params[key] = String(value).trim();
            }
        });

        dispatch(fetchObservationsActivitiesRequest(params));
    }, [dispatch, itemsPerPage, appliedFilters]);

    useEffect(() => {
        if (isAuthenticated) {
            loadObservations(currentPage);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAuthenticated, currentPage]);

    useEffect(() => {
        const loadPartners = async () => {
            if (!isAuthenticated) return;
            try {
                setPartnersLoading(true);
                setPartnersError(null);
                const response = await getAllPartners();
                let partnersList = [];
                if (response?.users && Array.isArray(response.users)) {
                    partnersList = response.users;
                } else if (Array.isArray(response)) {
                    partnersList = response;
                } else if (response?.partners && Array.isArray(response.partners)) {
                    partnersList = response.partners;
                } else if (response?.data && Array.isArray(response.data)) {
                    partnersList = response.data;
                }
                setPartners(partnersList);
            } catch (error) {
                setPartners([]);
                setPartnersError(handleApiError(error, 'Erreur lors du chargement des partenaires'));
            } finally {
                setPartnersLoading(false);
            }
        };
        loadPartners();
    }, [isAuthenticated]);

    const partnerCompanyOptions = useMemo(() => {
        const names = partners
            .map((partner) => partner.company_name?.trim())
            .filter((name) => name);
        return Array.from(new Set(names)).sort((a, b) => a.localeCompare(b, 'fr'));
    }, [partners]);

    const filteredPartnerCompanyOptions = useMemo(() => {
        const query = companyPartnerSearch.trim().toLowerCase();
        if (!query) return partnerCompanyOptions;
        return partnerCompanyOptions.filter((name) => name.toLowerCase().includes(query));
    }, [partnerCompanyOptions, companyPartnerSearch]);

    const observationsData = useMemo(() => {
        const payload = observationsState?.data;
        if (!payload) return [];
        if (Array.isArray(payload)) return payload;

        const nested = payload.data;
        if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
            if (Array.isArray(nested.records)) return nested.records;
            if (Array.isArray(nested.observations)) return nested.observations;
            if (Array.isArray(nested.data)) return nested.data;
        }

        const possibleArrays = [payload.observations, payload.records, payload.data];
        const found = possibleArrays.find((entry) => Array.isArray(entry));
        return Array.isArray(found) ? found : [];
    }, [observationsState]);

    const meta = useMemo(() => {
        const payload = observationsState?.data;
        const paginationMeta = payload?.meta || payload?.data?.meta;
        return {
            ...defaultMeta,
            ...(paginationMeta || {}),
        };
    }, [observationsState]);

    const totalPages = meta.total_pages || Math.max(1, Math.ceil((meta.total || 0) / itemsPerPage));

    const handleFilterChange = (event) => {
        const { name, value } = event.target;
        setFilters((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleCompanyPartnerSelect = (companyName) => {
        setCompanyPartnerSearch(companyName);
        setFilters((prev) => ({ ...prev, company_name: companyName }));
        setShowCompanyPartnerDropdown(false);
    };

    const handleCompanyPartnerSearchChange = (value) => {
        setCompanyPartnerSearch(value);
        setFilters((prev) => ({ ...prev, company_name: '' }));
        setShowCompanyPartnerDropdown(true);
    };

    const handleApplyFilters = () => {
        const companyName = (filters.company_name || companyPartnerSearch).trim();
        const nextFilters = { ...filters, company_name: companyName };
        setFilters(nextFilters);
        setCompanyPartnerSearch(companyName);
        setAppliedFilters(nextFilters);
        setCurrentPage(1);
        loadObservations(1, nextFilters);
    };

    const handleKeyPress = (event) => {
        if (event.key === 'Enter') {
            handleApplyFilters();
        }
    };

    const handleClearFilters = () => {
        const cleared = { ...EMPTY_ACTIVITY_OBSERVATIONS_FILTERS };
        clearSearchState();
        setTimeout(() => {
            loadObservations(1, cleared);
        }, 100);
    };

    const handleRefresh = () => {
        loadObservations(currentPage);
    };

    const getPaginationRange = () => {
        const maxVisiblePages = 5;
        let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
        let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

        if (endPage - startPage < maxVisiblePages - 1) {
            startPage = Math.max(1, endPage - maxVisiblePages + 1);
        }

        return { startPage, endPage };
    };

    const toggleRow = (rowId) => {
        setExpandedRows((prev) => ({
            ...prev,
            [rowId]: !prev[rowId],
        }));
    };

    const handleOpenDeleteModal = (item) => {
        setObservationToDelete(item);
        setShowDeleteModal(true);
        setDeleteError(null);
        setDeleteSuccess(null);
    };

    const handleCloseDeleteModal = () => {
        setShowDeleteModal(false);
        setObservationToDelete(null);
        setDeleteError(null);
        setDeleteSuccess(null);
    };

    const handleDelete = async () => {
        if (!observationToDelete) {
            return;
        }

        const observationId = getSafe(observationToDelete, ['last_observation_info', 'last_observation_id'], null);
        if (!observationId || observationId === 'N/A') {
            setDeleteError(t('pages.activityObservations.delete.error'));
            return;
        }

        try {
            setDeletingObservationId(observationId);
            setDeleteError(null);
            setDeleteSuccess(null);

            await deleteActivityObservation(observationId);

            const policyCode = getSafe(observationToDelete, ['policy_info', 'policy_code'], '');
            setDeleteSuccess(
                `${t('pages.activityObservations.delete.success')}${policyCode ? ` (${t('pages.activityObservations.table.policyCode')}: ${policyCode})` : ''}`
            );
            
            // Fermer la modal et recharger les observations après un court délai
            setTimeout(() => {
                handleCloseDeleteModal();
                loadObservations(currentPage);
                setDeleteSuccess(null);
            }, 1500);
        } catch (error) {
            let errorMsg = t('pages.activityObservations.delete.error');
            errorMsg = handleApiError(error, errorMsg);
            setDeleteError(errorMsg);
        } finally {
            setDeletingObservationId(null);
        }
    };

    const getRowId = (item, index) => {
        return (
            item?.policy_info?.policy_id ||
            item?.activity_info?.activity_id ||
            item?.last_observation_info?.last_observation_id ||
            item?.client_info?.client_id ||
            `row-${index}`
        );
    };

    return (
        <div className="dashboard-page admin-activity-observations-page">
            <Card>
                <Card.Body>
                    <div className="page-header">
                        <div>
                            <h2 className="page-title">
                                <FiBarChart2 className="me-2" />
                                {t('pages.activityObservations.title')}
                            </h2>
                            <p className="text-muted mb-0">{t('pages.activityObservations.subtitle')}</p>
                        </div>
                    </div>

                    {observationsState.error && (
                        <Alert variant="danger" className="mt-3">
                            {observationsState.error}
                        </Alert>
                    )}

                    <Card className="mb-3">
                        <Card.Header className="d-flex justify-content-between align-items-center">
                            <h6 className="mb-0">{t('common.filter')}</h6>
                            <div className="d-flex gap-2">
                                <Button variant="outline-secondary" size="sm" onClick={handleClearFilters}>
                                    {t('common.reset')}
                                </Button>
                                <Button variant="primary" size="sm" onClick={handleApplyFilters}>
                                    {t('common.apply')}
                                </Button>
                            </div>
                        </Card.Header>
                        <Card.Body>
                            <Row>
                                <Col md={4}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>{t('pages.activityObservations.filters.policyCode')}</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="policy_code"
                                            value={filters.policy_code}
                                            onChange={handleFilterChange}
                                            onKeyDown={handleKeyPress}
                                            placeholder="Ex: 821830993"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={4}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>{t('pages.activityObservations.filters.reservationNumber')}</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="reservation_number"
                                            value={filters.reservation_number}
                                            onChange={handleFilterChange}
                                            onKeyDown={handleKeyPress}
                                            placeholder="Numéro de réservation"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={4}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>{t('pages.activityObservations.filters.activityStartDate')}</Form.Label>
                                        <Form.Control
                                            type="date"
                                            name="activity_start_date"
                                            value={filters.activity_start_date}
                                            onChange={handleFilterChange}
                                            onKeyDown={handleKeyPress}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={4}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>{t('pages.activityObservations.filters.clientFirstName')}</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="client_first_name"
                                            value={filters.client_first_name}
                                            onChange={handleFilterChange}
                                            onKeyDown={handleKeyPress}
                                            placeholder="Prénom du client"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={4}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>{t('pages.activityObservations.filters.clientLastName')}</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="client_last_name"
                                            value={filters.client_last_name}
                                            onChange={handleFilterChange}
                                            onKeyDown={handleKeyPress}
                                            placeholder="Nom du client"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={4}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>{t('pages.activityObservations.filters.companyName')}</Form.Label>
                                        <div className="partner-autocomplete position-relative">
                                            <Form.Control
                                                type="text"
                                                value={companyPartnerSearch}
                                                onChange={(e) => handleCompanyPartnerSearchChange(e.target.value)}
                                                onFocus={() => setShowCompanyPartnerDropdown(true)}
                                                onBlur={() => {
                                                    setTimeout(() => setShowCompanyPartnerDropdown(false), 200);
                                                }}
                                                onKeyDown={handleKeyPress}
                                                placeholder={t('pages.activityObservations.filters.companyNamePlaceholder')}
                                                disabled={partnersLoading}
                                                autoComplete="off"
                                            />
                                            {companyPartnerSearch && (
                                                <Button
                                                    type="button"
                                                    variant="link"
                                                    className="partner-autocomplete-clear"
                                                    onMouseDown={(e) => e.preventDefault()}
                                                    onClick={() => handleCompanyPartnerSelect('')}
                                                    aria-label={t('common.reset')}
                                                >
                                                    ×
                                                </Button>
                                            )}
                                            {showCompanyPartnerDropdown && (
                                                <div className="partner-autocomplete-dropdown">
                                                    {partnersLoading ? (
                                                        <div className="partner-autocomplete-empty text-muted">
                                                            {t('pages.activityObservations.filters.partnersLoading')}
                                                        </div>
                                                    ) : filteredPartnerCompanyOptions.length === 0 ? (
                                                        <div className="partner-autocomplete-empty text-muted">
                                                            {t('pages.activityObservations.filters.noPartnerFound')}
                                                        </div>
                                                    ) : (
                                                        filteredPartnerCompanyOptions.map((companyName) => (
                                                            <button
                                                                key={companyName}
                                                                type="button"
                                                                className={`partner-autocomplete-option${filters.company_name === companyName ? ' active' : ''}`}
                                                                onMouseDown={(e) => e.preventDefault()}
                                                                onClick={() => handleCompanyPartnerSelect(companyName)}
                                                            >
                                                                {companyName}
                                                            </button>
                                                        ))
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                        {partnersError && (
                                            <Form.Text className="text-danger d-block mt-1">
                                                {partnersError}
                                            </Form.Text>
                                        )}
                                        <Form.Text className="text-muted">
                                            {t('pages.activityObservations.filters.companyNameHint')}
                                        </Form.Text>
                                    </Form.Group>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    <div className="d-flex justify-content-end gap-2 mb-3">
                        <Button
                            variant="outline-secondary"
                            onClick={handleRefresh}
                            disabled={observationsState.loading}
                            className="d-flex align-items-center"
                        >
                            <FiRefreshCw className={`me-2 ${observationsState.loading ? 'spinning' : ''}`} />
                            {t('common.refresh')}
                        </Button>
                    </div>

                    {observationsState.loading ? (
                        <div className="text-center py-5">
                            <Spinner animation="border" variant="primary" />
                            <p className="mt-3">{t('common.loading')}</p>
                        </div>
                    ) : observationsState.error ? (
                        <div className="text-center py-5">
                            <Alert variant="danger">{observationsState.error}</Alert>
                        </div>
                    ) : observationsData.length === 0 ? (
                        <div className="text-center py-5">
                            <FiInfo size={48} className="text-muted mb-3" />
                            <p className="text-muted">{t('pages.activityObservations.noData')}</p>
                        </div>
                    ) : (
                        <div className="table-responsive">
                            <div className="table-scroll">
                                <Table hover striped className="admin-table">
                                    <thead>
                                        <tr>
                                            <th>{t('pages.activityObservations.table.policyCode')}</th>
                                            <th>{t('pages.activityObservations.table.reservationNumber')}</th>
                                            <th>{t('pages.activityObservations.table.activityDate')}</th>
                                            <th>{t('pages.activityObservations.table.weatherStation')}</th>
                                            <th>{t('pages.activityObservations.table.totalRain')}</th>
                                            <th>{t('pages.activityObservations.table.triggeredObservation')}</th>
                                            <th className="text-end col-actions">{t('pages.activityObservations.table.actions')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {observationsData.map((item, index) => {
                                            const rowId = getRowId(item, index);
                                            const isExpanded = !!expandedRows[rowId];
                                            const stationName = getSafe(item, ['last_observation_info', 'last_station_name']);
                                            const stationId = getSafe(item, ['last_observation_info', 'last_station_id']);
                                            const totalPrecip = getSafe(item, ['last_observation_info', 'last_total_mm_prc'], '0');
                                            const isTriggeredObs = getSafe(item, ['CheckClaims', 'is_triggered_observation'], null);
                                            const triggeredObservation = isTriggeredObs === null ? null : isTriggeredObs;
                                            const activityStart = getSafe(item, ['activity_info', 'activity_start_date']);
                                            const activityEnd = getSafe(item, ['activity_info', 'activity_end_date']);
                                            const policyCode = getSafe(item, ['policy_info', 'policy_code']);
                                            const reservationNumber = getSafe(item, ['activity_info', 'reservation_number']);
                                            const observationId = getSafe(item, ['last_observation_info', 'last_observation_id'], 'N/A');

                                            return (
                                                <React.Fragment key={rowId}>
                                                    <tr className="observation-row">
                                                        <td style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>{policyCode}</td>
                                                        <td style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>{reservationNumber}</td>
                                                        <td>
                                                            {activityStart === activityEnd
                                                                ? formatDate(activityStart)
                                                                : `${formatDate(activityStart)} - ${formatDate(activityEnd)}`}
                                                        </td>
                                                        <td>
                                                            {stationName !== '-' ? (
                                                                <>
                                                                    {stationName}
                                                                    {stationId !== '-' && (
                                                                        <small className="text-muted d-block">ID: {stationId}</small>
                                                                    )}
                                                                </>
                                                            ) : (
                                                                '-'
                                                            )}
                                                        </td>
                                                        <td><strong>{totalPrecip} mm</strong></td>
                                                        <td>
                                                            {triggeredObservation === null ? (
                                                                <Badge bg="warning">{t('pages.activityObservations.pending')}</Badge>
                                                            ) : (
                                                                renderBooleanBadge(triggeredObservation)
                                                            )}
                                                        </td>
                                                        <td className="text-end col-actions">
                                                            {observationId !== 'N/A' && (
                                                                <>
                                                                    <Button
                                                                        variant="outline-info"
                                                                        size="sm"
                                                                        className="me-2"
                                                                        onClick={() => navigate(`/activity/observations/${observationId}`)}
                                                                        title={t('common.details')}
                                                                        aria-label={t('common.details')}
                                                                    >
                                                                        <FiEye />
                                                                    </Button>
                                                                    <Button
                                                                        variant="outline-danger"
                                                                        size="sm"
                                                                        onClick={() => handleOpenDeleteModal(item)}
                                                                        title={t('common.delete')}
                                                                        aria-label={t('common.delete')}
                                                                        disabled={deletingObservationId === observationId}
                                                                    >
                                                                        {deletingObservationId === observationId ? (
                                                                            <Spinner animation="border" size="sm" />
                                                                        ) : (
                                                                            <FiTrash2 />
                                                                        )}
                                                                    </Button>
                                                                </>
                                                            )}
                                                        </td>
                                                    </tr>
                                                    {isExpanded && (
                                                        <tr>
                                                            <td colSpan={7} className="p-0">
                                                                <Collapse in={isExpanded}>
                                                                    <div>
                                                                        <Card className="m-2">
                                                                            <Card.Body className="observation-details">
                                                                                <Row className="g-3">
                                                                                    <Col md={6}>
                                                                                        <div className="detail-card">
                                                                                            <h6>
                                                                                                <FiMapPin className="me-2" />
                                                                                                Station météo
                                                                                            </h6>
                                                                                            <ul className="detail-list">
                                                                                                <li>
                                                                                                    <strong>Nom :</strong> {stationName}
                                                                                                </li>
                                                                                                <li>
                                                                                                    <strong>ID :</strong> {stationId}
                                                                                                </li>
                                                                                                <li>
                                                                                                    <strong>Source :</strong>{' '}
                                                                                                    {getSafe(item, ['last_observation_info', 'last_data_source'], 'N/A')}
                                                                                                </li>
                                                                                                <li>
                                                                                                    <strong>Total pluie :</strong> {totalPrecip} mm
                                                                                                </li>
                                                                                            </ul>
                                                                                        </div>
                                                                                    </Col>
                                                                                    <Col md={6}>
                                                                                        <div className="detail-card">
                                                                                            <h6>
                                                                                                <FiInfo className="me-2" />
                                                                                                Observation
                                                                                            </h6>
                                                                                            <ul className="detail-list">
                                                                                                <li>
                                                                                                    <strong>Date init :</strong>{' '}
                                                                                                    {getSafe(item, ['last_observation_info', 'last_observation_init_day'], '--')}/
                                                                                                    {getSafe(item, ['last_observation_info', 'last_observation_init_month'], '--')}/
                                                                                                    {getSafe(item, ['last_observation_info', 'last_observation_init_year'], '----')}
                                                                                                </li>
                                                                                                <li>
                                                                                                    <strong>Heure exécution :</strong>{' '}
                                                                                                    {getSafe(item, ['last_observation_info', 'last_observation_run_hour'], '--')}
                                                                                                </li>
                                                                                                <li>
                                                                                                    <strong>Modifié le :</strong>{' '}
                                                                                                    {getSafe(item, ['last_observation_info', 'last_observation_last_modified_at'], 'N/A')}
                                                                                                </li>
                                                                                                <li>
                                                                                                    <strong>Modifié par :</strong>{' '}
                                                                                                    {getSafe(item, ['last_observation_info', 'last_observation_last_modified_by'], 'N/A')}
                                                                                                </li>
                                                                                            </ul>
                                                                                        </div>
                                                                                    </Col>
                                                                                    <Col md={6}>
                                                                                        <div className="detail-card">
                                                                                            <h6>
                                                                                                <FiUser className="me-2" />
                                                                                                Client
                                                                                            </h6>
                                                                                            <ul className="detail-list">
                                                                                                <li>
                                                                                                    <strong>Nom :</strong>{' '}
                                                                                                    {getSafe(item, ['client_info', 'first_name'], '')}{' '}
                                                                                                    {getSafe(item, ['client_info', 'last_name'], '')}
                                                                                                </li>
                                                                                                <li>
                                                                                                    <strong>Email :</strong> {getSafe(item, ['client_info', 'email'], 'N/A')}
                                                                                                </li>
                                                                                                <li>
                                                                                                    <strong>Statut :</strong> {getSafe(item, ['client_info', 'status'], 'N/A')}
                                                                                                </li>
                                                                                            </ul>
                                                                                        </div>
                                                                                    </Col>
                                                                                    <Col md={6}>
                                                                                        <div className="detail-card">
                                                                                            <h6>
                                                                                                <FiCalendar className="me-2" />
                                                                                                Activité
                                                                                            </h6>
                                                                                            <ul className="detail-list">
                                                                                                <li>
                                                                                                    <strong>Nom :</strong> {getSafe(item, ['activity_info', 'name'], 'N/A')}
                                                                                                </li>
                                                                                                <li>
                                                                                                    <strong>Dates :</strong> {formatDate(activityStart)} - {formatDate(activityEnd)}
                                                                                                </li>
                                                                                                <li>
                                                                                                    <strong>Ville :</strong> {getSafe(item, ['activity_info', 'activity_city'], 'N/A')}
                                                                                                </li>
                                                                                                <li>
                                                                                                    <strong>Pays :</strong> {getSafe(item, ['activity_info', 'activity_country'], 'N/A')}
                                                                                                </li>
                                                                                            </ul>
                                                                                        </div>
                                                                                    </Col>
                                                                                </Row>
                                                                            </Card.Body>
                                                                        </Card>
                                                                    </div>
                                                                </Collapse>
                                                            </td>
                                                        </tr>
                                                    )}
                                                </React.Fragment>
                                            );
                                        })}
                                    </tbody>
                                </Table>
                            </div>
                        </div>
                    )}

                    {!observationsState.loading && observationsData.length > 0 && totalPages > 1 && (
                        <div className="d-flex justify-content-center mt-4">
                            <Pagination>
                                <Pagination.First
                                    onClick={() => setCurrentPage(1)}
                                    disabled={currentPage === 1}
                                />
                                <Pagination.Prev
                                    onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                                    disabled={currentPage === 1}
                                />
                                {(() => {
                                    const { startPage, endPage } = getPaginationRange();
                                    const pages = [];
                                    for (let i = startPage; i <= endPage; i++) {
                                        pages.push(
                                            <Pagination.Item
                                                key={i}
                                                active={i === currentPage}
                                                onClick={() => setCurrentPage(i)}
                                            >
                                                {i}
                                            </Pagination.Item>
                                        );
                                    }
                                    return pages;
                                })()}
                                <Pagination.Next
                                    onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                                    disabled={currentPage === totalPages}
                                />
                                <Pagination.Last
                                    onClick={() => setCurrentPage(totalPages)}
                                    disabled={currentPage === totalPages}
                                />
                            </Pagination>
                        </div>
                    )}

                    {!observationsState.loading && observationsData.length > 0 && (
                        <div className="text-center mt-3 text-muted">
                            <small>
                                Affichage de {meta.skip + 1} à {Math.min(meta.skip + meta.limit, meta.total)} sur {meta.total}{' '}
                                observation{meta.total > 1 ? 's' : ''}
                            </small>
                        </div>
                    )}

                    {/* Modal de confirmation de suppression */}
                    <Modal show={showDeleteModal} onHide={handleCloseDeleteModal} centered>
                        <Modal.Header closeButton>
                            <Modal.Title>
                                <FiTrash2 className="me-2" />
                                {t('pages.activityObservations.delete.confirmTitle')}
                            </Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            <p>{t('pages.activityObservations.delete.confirmMessage')} {t('pages.activityObservations.delete.irreversible')}</p>
                            {observationToDelete && (
                                <div className="mt-3 p-3 bg-light rounded">
                                    <p className="mb-1">
                                        <strong>Code police :</strong> {getSafe(observationToDelete, ['policy_info', 'policy_code'], '—')}
                                    </p>
                                    <p className="mb-1">
                                        <strong>Réservation :</strong> {getSafe(observationToDelete, ['activity_info', 'reservation_number'], '—')}
                                    </p>
                                    <p className="mb-1">
                                        <strong>Date activité :</strong> {getSafe(observationToDelete, ['activity_info', 'activity_start_date'], '—')}
                                    </p>
                                    <p className="mb-1">
                                        <strong>Station météo :</strong> {getSafe(observationToDelete, ['last_observation_info', 'last_station_name'], '—')}
                                    </p>
                                    <p className="mb-0">
                                        <strong>Total pluie :</strong> {getSafe(observationToDelete, ['last_observation_info', 'last_total_mm_prc'], '0')} mm
                                    </p>
                                </div>
                            )}
                            {deleteError && (
                                <Alert variant="danger" className="mt-3">
                                    {deleteError}
                                </Alert>
                            )}
                            {deleteSuccess && (
                                <Alert variant="success" className="mt-3">
                                    {deleteSuccess}
                                </Alert>
                            )}
                        </Modal.Body>
                        <Modal.Footer>
                            <Button variant="secondary" onClick={handleCloseDeleteModal} disabled={!!deletingObservationId}>
                                Annuler
                            </Button>
                            <Button
                                variant="danger"
                                onClick={handleDelete}
                                disabled={!!deletingObservationId}
                            >
                                {deletingObservationId ? (
                                    <>
                                        <Spinner animation="border" size="sm" className="me-2" />
                                        {t('pages.activityObservations.delete.deleting')}
                                    </>
                                ) : (
                                    <>
                                        <FiTrash2 className="me-2" />
                                        Supprimer
                                    </>
                                )}
                            </Button>
                        </Modal.Footer>
                    </Modal>
                </Card.Body>
            </Card>
        </div>
    );
};

export default AdminActivityObservations;
